import { queryClient } from "@/shared/lib/react-query/query-client"
import { LANGUAGE_KEY } from "@/shared/lib/storage/device-preference"
import {
  AUTH_STORAGE_KEY,
  getAccessToken,
  removeTokens,
} from "@/shared/lib/token/token"
import camelcaseKeys from "camelcase-keys"
import { jwtDecode } from "jwt-decode"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import {
  AccountRoleEnum,
  DecodedAccessToken,
  UserInfo,
  UserLanguageEnum,
} from "@/entities/account/account.types"
import { DEFAULT_USER_FORM } from "./auth.constants"
import { Tokens } from "./auth.types"

export interface AuthState {
  user: UserInfo
  isAdmin: boolean
  isAuthenticated: boolean
  loadingUserData: boolean
  isAnonymousUser: boolean
  isKoreanLanguage: boolean
  has404Error: boolean
}

interface AuthAction {
  setUser: (tokens: Tokens & { email?: string }) => void
  logout: () => void
  handleSetUserData: (data: DecodedAccessToken) => void
  setHas404Error: (value: boolean) => void
  initializeAuth: () => void
  setUserByChangingFields: (fields: Partial<UserInfo>) => void
}

export const useAuthStore = create<AuthState & AuthAction>()(
  persist(
    (set, get) => ({
      user: DEFAULT_USER_FORM,
      isAdmin: false,
      isAuthenticated: false,
      loadingUserData: true,
      isAnonymousUser: true,
      isKoreanLanguage:
        localStorage.getItem(LANGUAGE_KEY) === UserLanguageEnum.Ko,
      has404Error: false,

      setUser: (tokens: Tokens & { email?: string }) => {
        const decodedUser = camelcaseKeys(
          jwtDecode<DecodedAccessToken>(tokens.accessToken)
        )
        set({
          user: {
            ...get().user,
            id: decodedUser.sub,
            userName: decodedUser.userName,
            role: decodedUser.role,
            companyName: decodedUser.companyName,
            phoneNumber: "",
            userLanguage: decodedUser.userLanguage,
            email: tokens.email || get().user.email || "", // 기존 email 유지
          },
          isAdmin: [
            AccountRoleEnum.Super,
            AccountRoleEnum.LeadPm,
            AccountRoleEnum.AssociatePm,
          ].includes(decodedUser.role),
          isAuthenticated: true,
          loadingUserData: false,
          isAnonymousUser: false,
          isKoreanLanguage: decodedUser.userLanguage === UserLanguageEnum.Ko,
        })
      },

      logout: () => {
        // A deliberate sign-out removes more than a session torn down under you
        // does: the two paths that reach removeTokens() directly -- a failed refresh
        // and a password reset -- do none of the four lines below. #1653 tracks
        // making all three agree.
        // AUTH_STORAGE_KEY is removed again after set() below, because the persist
        // middleware rewrites it on the state change.
        removeTokens()
        set({
          user: DEFAULT_USER_FORM,
          isAdmin: false,
          isAuthenticated: false,
          loadingUserData: false,
          isAnonymousUser: true,
          has404Error: false,
        })
        sessionStorage.clear()
        queryClient.clear()
        // Again, after set(): the persist middleware rewrote it on the state change.
        localStorage.removeItem(AUTH_STORAGE_KEY)
      },

      handleSetUserData: (data: DecodedAccessToken) => {
        const { user_language, user_name, sub, company_name, role } = data
        set((state) => {
          const currentUser = state.user

          return {
            user: {
              ...currentUser,
              id: sub,
              userName: user_name,
              role,
              companyName: company_name,
              phoneNumber: currentUser.phoneNumber || "",
              userLanguage: user_language,
              email: currentUser.email || "", // 기존 email 유지
            },
            isAdmin: [
              AccountRoleEnum.Super,
              AccountRoleEnum.LeadPm,
              AccountRoleEnum.AssociatePm,
            ].includes(role),
            loadingUserData: false,
            isAnonymousUser: false,
            isAuthenticated: true,
          }
        })
      },

      setHas404Error: (value: boolean) => set({ has404Error: value }),

      initializeAuth: () => {
        const accessToken = getAccessToken()
        if (accessToken) {
          const decodedData = jwtDecode<DecodedAccessToken>(accessToken)
          get().handleSetUserData(decodedData)
        } else {
          set({ loadingUserData: false })
        }
      },

      setUserByChangingFields: (fields: Partial<UserInfo>) => {
        set((state) => ({
          user: {
            ...state.user,
            ...fields,
          },
          isAdmin: fields.role
            ? [
                AccountRoleEnum.Super,
                AccountRoleEnum.LeadPm,
                AccountRoleEnum.AssociatePm,
              ].includes(fields.role)
            : state.isAdmin,
          isAnonymousUser: false,
        }))
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        isAdmin: state.isAdmin,
        isAuthenticated: state.isAuthenticated,
        isAnonymousUser: state.isAnonymousUser,
        isKoreanLanguage: state.isKoreanLanguage,
      }),
    }
  )
)

export const setLanguage = (language: string) => {
  useAuthStore.setState({
    isKoreanLanguage: language === UserLanguageEnum.Ko,
    user: {
      ...useAuthStore.getState().user,
      userLanguage: language as UserLanguageEnum,
    },
  })
}
